/**
 * Date formatting utilities for displaying dates in EST timezone
 */

/**
 * Formats a date string or Date object to a human-readable format in EST timezone
 * @param dateInput - ISO date string, Date object, or any valid date input
 * @param format - Format type: 'short' (MM/DD/YYYY), 'medium' (Sept 23, 2025), 'long' (September 23, 2025)
 * @returns Formatted date string in EST timezone
 */
export const formatPaymentDate = (
  dateInput: string | Date | null | undefined,
  format: 'short' | 'medium' | 'long' = 'medium'
): string => {
  if (!dateInput) {
    return '';
  }

  try {
    // Convert input to Date object
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return typeof dateInput === 'string' ? dateInput : '';
    }

    // Format options for EST timezone
    const estOptions: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: format === 'short' ? '2-digit' : format === 'medium' ? 'short' : 'long',
      day: '2-digit'
    };

    // Format the date
    const formatter = new Intl.DateTimeFormat('en-US', estOptions);
    const formatted = formatter.format(date);

    // For short format, ensure MM/DD/YYYY format
    if (format === 'short') {
      const parts = formatter.formatToParts(date);
      const month = parts.find(part => part.type === 'month')?.value || '';
      const day = parts.find(part => part.type === 'day')?.value || '';
      const year = parts.find(part => part.type === 'year')?.value || '';
      return `${month}/${day}/${year}`;
    }

    return formatted;
  } catch (error) {
    // Fallback: return original input as string if formatting fails
    return typeof dateInput === 'string' ? dateInput : '';
  }
};

/**
 * Formats a date for display in tables (medium format by default)
 * @param dateInput - Date input to format
 * @returns Formatted date string
 */
export const formatTableDate = (dateInput: string | Date | null | undefined): string => {
  return formatPaymentDate(dateInput, 'medium');
};

/**
 * Formats a date for compact display (short format)
 * @param dateInput - Date input to format
 * @returns Formatted date string in MM/DD/YYYY format
 */
export const formatCompactDate = (dateInput: string | Date | null | undefined): string => {
  return formatPaymentDate(dateInput, 'short');
};